import {
  AccordionGroup,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  LookbackOption,
  MixpanelEvent,
  Tab,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  tabLabelerEnumerated,
  useMixpanel,
  usePersistedTabs,
  usePortal,
  useTabs,
  type PersistedDefaultTabProps,
} from '@talos/kyoko';
import { useCallback, useState } from 'react';
import type { CustomerTradesTabProps } from '../../../types/LayoutConfig';
import { CustomerTradesBlotterTable } from './CustomerTradesBlotterTable';

const CUSTOMER_TRADES_BLOTTER_PREFIX = 'dealer/customer-trades';

const PRESET_TABS: PersistedDefaultTabProps<CustomerTradesTabProps>[] = [
  {
    label: 'Recent',
    id: 'recent-customer-trades',
    closable: false,
    editable: false,
    defaultFilter: {
      _start: LookbackOption.Past24Hours,
    },
    defaultColumns: [],
  },
];

const NEW_TAB_DEFAULTS: CustomerTradesTabProps = {
  defaultFilter: {
    _start: LookbackOption.Past24Hours,
  },
  defaultColumns: [],
};

const tabLabeler = tabLabelerEnumerated('Customer Trades');

export function CustomerTrades() {
  const mixpanel = useMixpanel();
  const [initialFiltersOpen, setInitialFiltersOpen] = useState(false);

  const handleSelect = useCallback(() => setInitialFiltersOpen(false), []);
  const handleAdd = useCallback(() => {
    mixpanel.track(MixpanelEvent.AddTab);
    setInitialFiltersOpen(true);
  }, [mixpanel]);

  const persistedTabs = usePersistedTabs<CustomerTradesTabProps>(CUSTOMER_TRADES_BLOTTER_PREFIX, {
    defaultInitialItems: PRESET_TABS,
    defaultInitialSelectedIndex: 0,
    excludedIDs: PRESET_TABS.map(tab => tab.id || ''),
    onSelect: handleSelect,
  });

  const tabs = useTabs<CustomerTradesTabProps>({
    ...persistedTabs,
    showAddTab: true,
    tabLabeler,
    onAdd: handleAdd,
  });

  const handleCloneTab = useCallback(() => {
    tabs.clone(tabs.selectedIndex);
  }, [tabs]);

  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  return (
    <Tabs {...tabs} style={{ height: '100%' }} size={TabSize.Large}>
      <TabList flex="0 0 auto" isBordered rightItems={<Box ref={filtersContainerRef} />}>
        {tabs.items.map((tab, idx) => (
          <Tab key={idx} {...tab} reorderable={true} />
        ))}
      </TabList>
      <TabPanels style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
        {tabs.items.map((tab, idx) => (
          <AccordionGroup key={tab.id}>
            <CustomerTradesBlotterTable
              key={idx}
              blotterID={`${CUSTOMER_TRADES_BLOTTER_PREFIX}/${tab.id}`}
              tabLabel={tab.label}
              defaultColumns={'defaultColumns' in tab ? tab.defaultColumns : NEW_TAB_DEFAULTS.defaultColumns}
              defaultFilter={'defaultFilter' in tab ? tab.defaultFilter : NEW_TAB_DEFAULTS.defaultFilter}
              initialIsOpen={initialFiltersOpen}
              onCloneTab={handleCloneTab}
            />
          </AccordionGroup>
        ))}
      </TabPanels>
    </Tabs>
  );
}
